import { motion } from "framer-motion";
import { Overline, OrnamentDivider, GoldDivider } from "@/components/GoldDivider";
import CtaSection from "@/components/CtaSection";
import PhotoGrid from "@/components/PhotoGrid";
import usePhotos from "@/hooks/usePhotos";
import { IMAGES } from "@/data/content";

const fadeUp = {
  hidden: { opacity: 0, y: 24 },
  show: (i = 0) => ({ opacity: 1, y: 0, transition: { duration: 0.7, delay: i * 0.1, ease: [0.16, 1, 0.3, 1] } }),
};

const VALUES = [
  { title: "We measure twice.", body: "Precision is not a tagline. It is field measurements, 3D shop drawings and a design you sign off on." },
  { title: "We build what we sell.", body: "No outsourcing to a factory. The people who quote your job are the people who build it." },
  { title: "We answer our phone.", body: "When you call West Ouachita, you talk to West Ouachita. Referrals are our whole business." },
];

export default function About() {
  const { images, loading } = usePhotos("about");

  return (
    <div className="bg-ivory">
      <section className="pt-36 pb-24 px-6 velvet-deep" data-testid="about-hero">
        <div className="max-w-7xl mx-auto grid lg:grid-cols-12 gap-12 items-center">
          <motion.div initial="hidden" whileInView="show" viewport={{ once: true }} variants={fadeUp} className="lg:col-span-6">
            <Overline>Our Story</Overline>
            <OrnamentDivider className="mt-6 !justify-start" width={200} />
            <h1 className="font-serif text-5xl sm:text-6xl font-light text-ivory mt-5 leading-[1.05]">
              Built on a name, not a brochure.
            </h1>
            <p className="text-[#A9B7CE] text-lg mt-6 leading-relaxed">
              West Ouachita Cabinets started the way the best shops do: one craftsman, a refusal to hand off work he
              would not be proud to sign, and a promise to the families of North Louisiana.
            </p>
            <p className="text-[#A9B7CE] text-lg mt-4 leading-relaxed">
              Since 1998 that standard has not moved. We have grown into a West Monroe shop outfitted with
              state-of-the-art CNC machinery and precision equipment, yet every cabinet still gets the same attention
              as the first. This is cabinetry for people who notice the difference, and for the builders and designers
              who trust us with their name too.
            </p>
            <GoldDivider className="mt-8 max-w-[120px]" />
          </motion.div>
          <motion.div initial="hidden" whileInView="show" viewport={{ once: true }} variants={fadeUp} custom={1} className="lg:col-span-6">
            <img src={IMAGES.craftHands} alt="Precision machinery in the West Ouachita shop" className="w-full h-[480px] object-cover" />
          </motion.div>
        </div>
      </section>

      {/* How we work */}
      <section className="py-24 px-6" data-testid="how-we-work">
        <div className="max-w-7xl mx-auto">
          <div className="max-w-2xl">
            <Overline>How We Work</Overline>
            <h2 className="font-serif text-4xl sm:text-5xl font-light text-navy mt-4">Three promises we do not break.</h2>
          </div>
          <div className="grid md:grid-cols-3 gap-6 mt-14">
            {VALUES.map((v, i) => (
              <motion.div key={v.title} initial="hidden" whileInView="show" viewport={{ once: true }} variants={fadeUp} custom={i}
                className="bg-white border border-navy/10 p-8">
                <span className="font-serif text-5xl text-gold/40 font-light">0{i + 1}</span>
                <h3 className="font-serif text-2xl text-navy mt-3">{v.title}</h3>
                <p className="text-navy/70 mt-2 leading-relaxed">{v.body}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Uploadable about photos */}
      <section className="bg-ivory pb-24 px-6" data-testid="about-photos">
        <div className="max-w-7xl mx-auto">
          <div className="text-center max-w-2xl mx-auto mb-10">
            <Overline>The Team & The Shop</Overline>
            <h2 className="font-serif text-4xl font-light text-navy mt-4">Faces behind the finish.</h2>
          </div>
          <PhotoGrid images={images} loading={loading} columns={3} placeholderCount={3} minSlots={3}
            placeholderNote="Add team photos or shop shots here from the admin dashboard (About section)." />
        </div>
      </section>

      <CtaSection />
    </div>
  );
}
